import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { logAuthEvent } from "@/lib/authAudit";
import { Button } from "@/components/ui/button";

/**
 * OAuth callback landing page for SSO sign-ins (Microsoft Entra via Supabase GoTrue).
 *
 * Handles both PKCE (?code=...) and implicit (#access_token=...) responses, plus
 * provider error responses (?error=...&error_description=...).
 */
export default function AuthCallback() {
  const navigate = useNavigate();
  const [message, setMessage] = useState("Finishing sign-in…");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [debugInfo, setDebugInfo] = useState<string>("");

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const href = window.location.href;
        const search = new URLSearchParams(window.location.search);
        const hash = new URLSearchParams(
          window.location.hash.startsWith("#") ? window.location.hash.slice(1) : window.location.hash
        );
        console.log("[AuthCallback] landed href=", href);
        console.log("[AuthCallback] search params=", Object.fromEntries(search.entries()));
        console.log("[AuthCallback] hash params=", Object.fromEntries(hash.entries()));
        setDebugInfo(`host=${window.location.host} search=${window.location.search || "(none)"} hash=${window.location.hash || "(none)"}`);

        // 1. Provider error response surfaced in URL.
        const providerError = search.get("error") || hash.get("error");
        const providerErrorDesc =
          search.get("error_description") || hash.get("error_description");
        if (providerError) {
          const msg = `${providerError}: ${providerErrorDesc ?? "no description"}`;
          console.error("[AuthCallback] provider error", msg);
          logAuthEvent(null, "unknown", "login_failed", { provider_error: msg });
          if (!cancelled) {
            setErrorMsg(`Microsoft sign-in failed — ${msg}`);
            setMessage("");
          }
          return;
        }

        // 2. PKCE: exchange ?code= for a session if present.
        const code = search.get("code");
        if (code) {
          console.log("[AuthCallback] exchanging code for session");
          const { data, error } = await supabase.auth.exchangeCodeForSession(href);
          console.log("[AuthCallback] exchangeCodeForSession result", { hasSession: !!data?.session, error });
          if (error) {
            logAuthEvent(null, "unknown", "login_failed", { exchange_error: error.message });
            if (!cancelled) {
              setErrorMsg(`Sign-in exchange failed — ${error.message}`);
              setMessage("");
            }
            return;
          }
        }

        // 3. Wait briefly for session (implicit flow / detectSessionInUrl).
        let session: any = null;
        for (let i = 0; i < 30; i++) {
          const { data } = await supabase.auth.getSession();
          if (data.session) {
            session = data.session;
            break;
          }
          await new Promise((r) => setTimeout(r, 200));
        }
        if (cancelled) return;

        if (!session) {
          console.warn("[AuthCallback] no session after wait");
          setErrorMsg("Sign-in did not complete. No session was established.");
          setMessage("");
          return;
        }

        const userId = session.user?.id ?? null;
        const email = session.user?.email ?? "unknown";
        console.log("[AuthCallback] session established for", { userId, email });

        // 4. Resolve roles. If none → orphan page.
        const { data: roles, error: rolesErr } = await supabase.rpc("current_user_roles");
        if (rolesErr) console.warn("[AuthCallback] current_user_roles error", rolesErr);
        const roleList = Array.isArray(roles) ? roles.filter(Boolean) : [];
        console.log("[AuthCallback] roles=", roleList);

        // 5. Record SSO amr (informational — v1 does not trust Entra MFA).
        try {
          const amr: string[] = Array.isArray((session.user as any)?.amr)
            ? (session.user as any).amr
            : [];
          await supabase.rpc("sso_record_login", {
            p_subject: session.user?.user_metadata?.sub ?? session.user?.id ?? "",
            p_idp_tenant_id: null as any,
            p_amr: amr,
          });
        } catch (e) {
          console.warn("[AuthCallback] sso_record_login non-fatal error", e);
        }

        if (roleList.length === 0) {
          logAuthEvent(userId, email, "sso_login_orphan");
          navigate("/access-not-provisioned", { replace: true });
          return;
        }

        // v1 SSO policy: Entra/OIDC login is restricted to PM Tier 2, Reviewer, Respondent.
        // Admin and PM Tier 1 must use local password login.
        const rolesLower = roleList.map((r: any) => String(r).toLowerCase());
        let denyReason: string | null = null;
        if (rolesLower.includes("admin") || rolesLower.includes("super_admin")) {
          denyReason = "admin_sso_not_allowed";
        } else if (rolesLower.includes("portfolio_manager")) {
          const { data: tier, error: tierErr } = await supabase.rpc("get_my_tier");
          if (tierErr) console.warn("[AuthCallback] get_my_tier error", tierErr);
          console.log("[AuthCallback] PM tier=", tier);
          if (String(tier ?? "").toLowerCase() === "tier_1") {
            denyReason = "pm_tier1_sso_not_allowed";
          }
        }

        if (denyReason) {
          console.warn("[AuthCallback] SSO denied:", denyReason);
          logAuthEvent(userId, email, "sso_role_denied", { reason: denyReason, roles: rolesLower });
          await supabase.auth.signOut();
          navigate(`/access-not-provisioned?reason=${denyReason}`, { replace: true });
          return;
        }

        logAuthEvent(userId, email, "sso_login_success");
        navigate("/post-login", { replace: true });
      } catch (e: any) {
        console.error("[AuthCallback] unexpected error", e);
        if (!cancelled) {
          setErrorMsg(`Unexpected error: ${e?.message ?? String(e)}`);
          setMessage("");
        }
      }
    };

    run();
    return () => {
      cancelled = true;
    };
  }, [navigate]);

  if (errorMsg) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-background px-6">
        <div className="max-w-lg w-full bg-card border border-border rounded-xl p-8 text-center">
          <div className="text-destructive text-5xl mb-4">⚠️</div>
          <h1 className="text-2xl font-bold mb-2">Sign-in failed</h1>
          <p className="text-muted-foreground mb-4">{errorMsg}</p>
          {debugInfo && (
            <pre className="text-xs text-left bg-muted p-3 rounded-md overflow-auto mb-4 whitespace-pre-wrap break-all">
              {debugInfo}
            </pre>
          )}
          <Button onClick={() => (window.location.href = "/login")} className="w-full">
            Back to login
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      <div className="text-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-2"></div>
        <p className="text-muted-foreground">{message}</p>
      </div>
    </div>
  );
}
